import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Stack,
  Grid,
  FormLabel,
  TextField,
  Checkbox,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useSelector } from "react-redux";
import { useStyles } from "./paymentUpdatesContainerStyles";
import { CommonPaginationContainer, CommonFilterSidebar, commonToast, CommonSelect } from "../common";
import { CommonButton } from "../common/commonButton";
import instance from "../../redux/api";
import { paymentTreasury, getAllPaymentIds } from "../../Services/api";
import { getPaginationCount } from "../CashAllocation/utils";
import "../common/commonPageStyles.css";
import {
  primaryButtonStyle,
  primaryButtonHoverColor,
} from "../common/commonButtonStyles";



const TreasuryList = () => {
  const { search } = useLocation();
  const policyNo = new URLSearchParams(search).get("policyNo");
  const fileName = new URLSearchParams(search).get("fileName");
  const paymentType = new URLSearchParams(search).get("paymentType");

  const [paginationCount, setPaginationCount] = useState(0);
  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [isClearPerformed, setIsClearPerformed] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);

  // Filter Data States
  const [availablePaymentTypes] = useState([
    "Syndicate Payment",
    "Internal Broking",
    "MGA Commission",
    "Rebate",
  ]);
  const [availablePaymentIds, setAvailablePaymentIds] = useState([]);
  const [availableFileNames, setAvailableFileNames] = useState([]);
  const [availablePolicyNos, setAvailablePolicyNos] = useState([]);

  const [searchFieldsParams, setSearchFieldsParams] = useState({
    type_of_payment: paymentType ? [paymentType] : [],
    policy_no: policyNo ? [policyNo] : [],
    bdx_file_name: fileName ? [fileName] : [],
    payment_id: [],
  });

  const { userData } = useSelector((state) => state?.user);

  function handleAmount(val) {
    const options = {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };

    return (
      (Number(val) && Number(val).toLocaleString("en-US", options)) ||
      (val === 0 && parseInt(val).toFixed(2)) ||
      val
    );
  }

  const toggle = useSelector((state) => state.toggleSideMenu);

  const currentSkipNumber = () => {
    return pageState?.page === 1
      ? 0
      : pageState?.page === 0
        ? pageState?.page
        : pageState?.page - 1;
  };

  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
  });

  //styles
  const classes = useStyles();

  const Container = {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 0,
    height: "calc(100vh - 180px)",
    maxHeight: "calc(100vh - 180px)",
    overflow: "hidden",
    backgroundColor: "#fafafa",
  };

  // Helper to build query string from array params
  const buildQueryParams = (singlePaymentType, singlePolicyNo, singleFileName, singlePaymentId) => {
    let queryParams = "";

    if (singlePaymentType) {
      queryParams = `${queryParams}&type_of_payment=${encodeURIComponent(singlePaymentType)}`;
    }

    if (singlePolicyNo) {
      queryParams = `${queryParams}&certificate_ref=${encodeURIComponent(singlePolicyNo)}`;
    }

    if (singleFileName) {
      queryParams = `${queryParams}&file_name=${encodeURIComponent(singleFileName)}`;
    }

    if (singlePaymentId) {
      queryParams = `${queryParams}&payment_id=${encodeURIComponent(singlePaymentId)}`;
    }

    return queryParams;
  };

  const loadSearchTxnData = async () => {
    try {
      setPageState((old) => ({ ...old, isLoading: true }));
      setIsSearchPerformed(true);
      setIsClearPerformed(false);

      const { type_of_payment, policy_no, bdx_file_name, payment_id } = searchFieldsParams;

      const paymentTypes = Array.isArray(type_of_payment) ? type_of_payment : (type_of_payment ? [type_of_payment] : []);
      const policyNos = Array.isArray(policy_no) ? policy_no : (policy_no ? [policy_no] : []);
      const fileNames = Array.isArray(bdx_file_name) ? bdx_file_name : (bdx_file_name ? [bdx_file_name] : []);
      const pIds = Array.isArray(payment_id) ? payment_id : (payment_id ? [payment_id] : []);

      // If no filters selected, load basic data
      if (paymentTypes.length === 0 && policyNos.length === 0 && fileNames.length === 0 && pIds.length === 0) {
        let queryParams = `?skip=0&pageSize=${pageState?.pageSize}`;
        const response = await instance.get(`${paymentTreasury}${queryParams}`);
        if (response.status === 200) {
          setPageState((old) => ({
            ...old,
            isLoading: false,
            total: response?.data?.count,
            data: response?.data?.data ?? [],
            page: 1,
          }));
          setPaginationCount(getPaginationCount(response?.data?.count || 0, pageState?.pageSize));
        }
        return;
      }

      // Combine filters
      // Since backend likely doesn't support array params like ?id=1,2,3 for all fields (based on previous code),
      // we generate combinations and query. 

      const ptCombos = paymentTypes.length > 0 ? paymentTypes : [null];
      const pnCombos = policyNos.length > 0 ? policyNos : [null];
      const fnCombos = fileNames.length > 0 ? fileNames : [null];
      const piCombos = pIds.length > 0 ? pIds : [null];

      const allResults = [];
      const seenIds = new Set();
      let maxCount = 0;

      const allCombinations = [];
      ptCombos.forEach(pt => {
        pnCombos.forEach(pn => {
          fnCombos.forEach(fn => {
            piCombos.forEach(pi => {
              allCombinations.push({ pt, pn, fn, pi });
            });
          });
        });
      });

      // Execute queries
      for (const combo of allCombinations) {
        const queryParams = buildQueryParams(combo.pt, combo.pn, combo.fn, combo.pi);
        const fullQuery = `?skip=0&pageSize=${pageState?.pageSize}${queryParams}`;

        try {
          const response = await instance.get(`${paymentTreasury}${fullQuery}`);
          if (response.status === 200 && response.data?.data) {
            response.data.data.forEach(item => {
              if (!seenIds.has(item.id)) {
                seenIds.add(item.id);
                allResults.push(item);
              }
            });
            maxCount = Math.max(maxCount, response.data.count || 0);
          }
        } catch (error) {
          console.error("Error in combination query:", error);
        }
      }

      setPageState((old) => ({
        ...old,
        isLoading: false,
        total: allResults.length,
        data: allResults,
        page: 1,
      }));
      setPaginationCount(getPaginationCount(allResults.length, pageState?.pageSize));

    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      console.log("err", err);
    }
  };

  const loadData = async () => {
    try {
      setPageState((old) => ({
        ...old,
        isLoading: true,
      }));

      let queryParams = `?skip=${currentSkipNumber()}&pageSize=${pageState?.pageSize}`;

      if (isSearchPerformed) {
        const { type_of_payment, policy_no, bdx_file_name, payment_id } = searchFieldsParams;
        const pt = Array.isArray(type_of_payment) && type_of_payment.length > 0 ? type_of_payment[0] : null;
        const pn = Array.isArray(policy_no) && policy_no.length > 0 ? policy_no[0] : null;
        const fn = Array.isArray(bdx_file_name) && bdx_file_name.length > 0 ? bdx_file_name[0] : null;
        const pi = Array.isArray(payment_id) && payment_id.length > 0 ? payment_id[0] : null;

        const extraParams = buildQueryParams(pt, pn, fn, pi);
        if (extraParams) {
          queryParams = `${queryParams}${extraParams}`;
        }
      } else {
        // handle URL params if not search performed yet
        if (paymentType) queryParams += `&type_of_payment=${paymentType}`;
        if (policyNo) queryParams += `&certificate_ref=${policyNo}`;
        if (fileName) queryParams += `&file_name=${fileName}`;
      }

      const response = await instance.get(`${paymentTreasury}${queryParams}`);

      setPageState((old) => ({
        ...old,
        isLoading: false,
        total: response?.data?.count,
        data: response?.data?.data ?? [],
      }));
      setPaginationCount(
        getPaginationCount(response?.data?.count, pageState?.pageSize)
      );
    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.error("err", err);
    }
  };

  const handleClearSearchList = async () => {
    setIsSearchPerformed(false);
    setIsClearPerformed(true);

    setSearchFieldsParams({
      type_of_payment: [],
      policy_no: [],
      bdx_file_name: [],
      payment_id: [],
    });
  };

  const isFilterBtnDisable = () => {
    const { type_of_payment, policy_no, bdx_file_name, payment_id } = searchFieldsParams;
    return !(
      (Array.isArray(type_of_payment) && type_of_payment.length > 0) ||
      (Array.isArray(policy_no) && policy_no.length > 0) ||
      (Array.isArray(bdx_file_name) && bdx_file_name.length > 0) ||
      (Array.isArray(payment_id) && payment_id.length > 0)
    );
  };

  // Fetch filter options
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Fetch Payment IDs
        const idsResponse = await instance.get(`${getAllPaymentIds}`);
        setAvailablePaymentIds(idsResponse?.data?.data || []);

        // Fetch Data for Policy No and File Name
        const response = await instance.get(`${paymentTreasury}?skip=0&pageSize=10000`);
        if (response.status === 200 && response.data && response.data.data) {
          const allData = response.data.data;

          const fileNamesSet = new Set();
          const policyNosSet = new Set();

          allData.forEach(item => {
            if (item.file_name) fileNamesSet.add(item.file_name);
            if (item.certificate_ref) policyNosSet.add(item.certificate_ref);
          });

          setAvailableFileNames(Array.from(fileNamesSet).sort());
          setAvailablePolicyNos(Array.from(policyNosSet).sort());
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }
    };

    fetchFilterData();
  }, []);

  useEffect(() => {
    if (isClearPerformed) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClearPerformed]);

  useEffect(() => {
    if (!isClearPerformed) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState?.pageSize, pageState?.page]);




  const syndicatePaymentColumns = useMemo(() => [
    {
      field: "source_input",
      headerName: "Input Source",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "file_name",
      headerName: "File Name",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "certificate_ref",
      headerName: "Policy Number",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "coverholder_name",
      headerName: "Coverholder Name",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "coverholder_entity",
      headerName: "Coverholder Entity",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "payment_id",
      headerName: "Payment ID",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "type_of_payment",
      headerName: "Payment Type",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "bank_account",
      headerName: "Bank Account",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "sett_currency",
      headerName: "Settlement Currency",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "net_transfer",
      headerName: "Net Transfer",
      headerAlign: "center",
      width: 160,
      valueFormatter: params => handleAmount(params.value),
    },
    {
      field: "payment_date",
      headerName: "Payment Date",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "payment_status",
      headerName: "Payment Status",
      headerAlign: "center",
      width: 160,
    },
  ], []);

  const commissionPaymentColumns = useMemo(() => [
    {
      field: "file_name",
      headerName: "File Name",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "certificate_ref",
      headerName: "Policy Number",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "category",
      headerName: "Category",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "coverholder_name",
      headerName: "Coverholder Name",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "receiving_bank_account",
      headerName: "Bank Account",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "settlement_currency",
      headerName: "Settlement Currency",
      headerAlign: "center",
      width: 160,
    },
  ], []);

  const rebatePaymentColumns = useMemo(() => [
    {
      field: "source_input",
      headerName: "Input Source",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "file_name",
      headerName: "File Name",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "certificate_ref",
      headerName: "Policy Number",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "coverholder_name",
      headerName: "Coverholder Name",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "coverholder_entity",
      headerName: "Coverholder Entity",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "payment_id",
      headerName: "Payment ID",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "type_of_payment",
      headerName: "Payment Type",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "bank_account",
      headerName: "Bank Account",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "sett_currency",
      headerName: "Settlement Currency",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "net_transfer",
      headerName: "Net Transfer",
      headerAlign: "center",
      width: 160,
      valueFormatter: params => handleAmount(params.value),
    },
    {
      field: "payment_date",
      headerName: "Payment Date",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "payment_status",
      headerName: "Payment Status",
      headerAlign: "center",
      width: 160,
    },
  ], []);

  const partnerPaymentColumns = useMemo(() => [
    {
      field: "source_input",
      headerName: "Input Source",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "file_name",
      headerName: "File Name",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "certificate_ref",
      headerName: "Policy Number",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "coverholder_name",
      headerName: "Coverholder Name",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "coverholder_entity",
      headerName: "Coverholder Entity",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "payment_id",
      headerName: "Payment ID",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "type_of_payment",
      headerName: "Payment Type",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "bank_account",
      headerName: "Bank Account",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "sett_currency",
      headerName: "Settlement Currency",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "net_transfer",
      headerName: "Net Transfer",
      headerAlign: "center",
      width: 160,
      valueFormatter: params => handleAmount(params.value),
    },
    {
      field: "payment_date",
      headerName: "Payment Date",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "payment_status",
      headerName: "Payment Status",
      headerAlign: "center",
      width: 160,
    },
  ], []);

  const treasuryPaymentTypes = useMemo(() => {
    // Determine payment type from search parameters or default
    // Using the first selected payment type if available, otherwise just default
    const currentPaymentType = Array.isArray(searchFieldsParams.type_of_payment) && searchFieldsParams.type_of_payment.length > 0
      ? searchFieldsParams.type_of_payment[0]
      : "";

    switch (currentPaymentType) {
      case "Syndicate Payment":
        return [...syndicatePaymentColumns];
      case "Internal Broking":
        return [...partnerPaymentColumns];
      case "MGA Commission":
        return [...commissionPaymentColumns];
      case "Rebate":
        return [...rebatePaymentColumns];
      default:
        return [...syndicatePaymentColumns];
    }
  }, [searchFieldsParams.type_of_payment, syndicatePaymentColumns, partnerPaymentColumns, commissionPaymentColumns, rebatePaymentColumns]);

  const filterConfigs = [
    {
      type: "bankName",
      label: "Payment Type",
      placeholder: "Search Payment Type",
      items: availablePaymentTypes,
      selectedItems: searchFieldsParams.type_of_payment,
      searchValue: "",
      onSearchChange: () => { },
      onItemChange: (value) => {
        const current = searchFieldsParams.type_of_payment;
        if (current.includes(value)) {
          setSearchFieldsParams(v => ({ ...v, type_of_payment: current.filter(i => i !== value) }));
        } else {
          setSearchFieldsParams(v => ({ ...v, type_of_payment: [...current, value] }));
        }
      },
      onClear: () => setSearchFieldsParams(v => ({ ...v, type_of_payment: [] })),
    },
    {
      type: "bankName",
      label: "Payment ID",
      placeholder: "Search Payment ID",
      items: availablePaymentIds,
      selectedItems: searchFieldsParams.payment_id,
      searchValue: "",
      onSearchChange: () => { },
      onItemChange: (value) => {
        const current = searchFieldsParams.payment_id;
        if (current.includes(value)) {
          setSearchFieldsParams(v => ({ ...v, payment_id: current.filter(i => i !== value) }));
        } else {
          setSearchFieldsParams(v => ({ ...v, payment_id: [...current, value] }));
        }
      },
      onClear: () => setSearchFieldsParams(v => ({ ...v, payment_id: [] })),
    },
    {
      type: "bankName",
      label: "Policy No",
      placeholder: "Search Policy No",
      items: availablePolicyNos,
      selectedItems: searchFieldsParams.policy_no,
      searchValue: "",
      onSearchChange: () => { },
      onItemChange: (value) => {
        const current = searchFieldsParams.policy_no;
        if (current.includes(value)) {
          setSearchFieldsParams(v => ({ ...v, policy_no: current.filter(i => i !== value) }));
        } else {
          setSearchFieldsParams(v => ({ ...v, policy_no: [...current, value] }));
        }
      },
      onClear: () => setSearchFieldsParams(v => ({ ...v, policy_no: [] })),
    },
    {
      type: "bankName",
      label: "File Name",
      placeholder: "Search File Name",
      items: availableFileNames,
      selectedItems: searchFieldsParams.bdx_file_name,
      searchValue: "",
      onSearchChange: () => { },
      onItemChange: (value) => {
        const current = searchFieldsParams.bdx_file_name;
        if (current.includes(value)) {
          setSearchFieldsParams(v => ({ ...v, bdx_file_name: current.filter(i => i !== value) }));
        } else {
          setSearchFieldsParams(v => ({ ...v, bdx_file_name: [...current, value] }));
        }
      },
      onClear: () => setSearchFieldsParams(v => ({ ...v, bdx_file_name: [] })),
    },
  ];

  return (
    <div style={Container}>
      <CommonFilterSidebar
        isOpen={filterSidebarOpen}
        filterConfigs={filterConfigs}
        onApplyFilters={loadSearchTxnData}
        onClearFilters={handleClearSearchList}
        isLoading={pageState?.isLoading}
        isFilterDisabled={isFilterBtnDisable()}
      />

      <Box className="main-content-area">
        <Box className="page-header-container">
          <Typography variant="h5" className="page-heading">
            Treasury List
          </Typography>
          <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <IconButton
              onClick={() => setFilterSidebarOpen(!filterSidebarOpen)}
              className="filter-toggle-button"
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            position: "relative",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            overflow: "visible",
            transition: "all 0.3s ease",
            border: "1px solid #e8eaf0",
            paddingBottom: "10px",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            },
          }}
        >
          <DataGrid
            className={classes.pagination}
            loading={pageState?.isLoading}
            rows={pageState?.data ?? []}
            getRowId={(row) => row?.id ?? ""}
            rowCount={pageState?.total}
            columns={treasuryPaymentTypes}
            disableRowSelectionOnClick
            keepNonExistentRowsSelected
            pagination
            paginationMode="server"
            disableSelectionOnClick
            page={pageState.page - 1}
            pageSize={pageState?.pageSize ?? 25}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageSizeChange={(newPageSize) => {
              return setPageState((old) => {
                setPaginationCount(
                  getPaginationCount(pageState?.total, newPageSize),
                );
                return { ...old, pageSize: newPageSize, page: 1 };
              });
            }}
            sx={{
              border: "none",
              flex: 1,
              minHeight: 0,
              "& .MuiDataGrid-root": {
                border: "none",
                height: "100%",
              },
              "& .MuiDataGrid-main": {
                height: "100%",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#1672DE",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#1565c0",
                  },
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #e0e0e0",
                minHeight: "52px !important",
                maxHeight: "52px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "transparent",
                fontWeight: "600",
                color: "#1672DE",
                fontSize: "14px",
                padding: "12px 16px",
                borderBottom: "none",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "600",
                  fontSize: "14px",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#1672DE",
                },
                "& .MuiDataGrid-menuIcon": {
                  color: "#1672DE",
                },
              },
              "& .MuiDataGrid-row": {
                transition: "all 0.2s ease",
                borderBottom: "1px solid #f0f0f0",
                "&:hover": {
                  backgroundColor: "rgba(22, 114, 222, 0.04)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(22, 114, 222, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.12)",
                  },
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "#fafafa",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.04)",
                  },
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
                padding: "12px 16px",
                fontSize: "13px",
                color: "#333",
                display: "flex",
                alignItems: "center",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#ccc",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#bbb",
                  },
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid #e0e0e0",
                backgroundColor: "#f8f9fa",
                minHeight: "48px",
                maxHeight: "48px",
                paddingBottom: "8px",
                paddingTop: "8px",
                paddingLeft: "16px",
                paddingRight: "16px",
                position: "relative",
                zIndex: 1,
                "& .MuiTablePagination-root": {
                  color: "#666",
                  paddingRight: "16px",
                  margin: 0,
                  minHeight: "32px",
                },
                "& .MuiTablePagination-selectLabel": {
                  fontSize: "13px",
                  margin: 0,
                },
                "& .MuiTablePagination-displayedRows": {
                  fontSize: "13px",
                  margin: 0,
                },
                "& .MuiTablePagination-actions": {
                  marginLeft: "8px",
                  "& .MuiIconButton-root": {
                    padding: "4px",
                  },
                },
                "& .MuiTablePagination-select": {
                  margin: "0 8px",
                  padding: "4px 8px",
                },
                "& .MuiTablePagination-toolbar": {
                  minHeight: "32px",
                  padding: 0,
                },
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "12px 16px",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #e0e0e0",
                "& .MuiButton-root": {
                  color: "#1672DE",
                  fontSize: "13px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.1)",
                  },
                },
              },
              "& .MuiDataGrid-loadingOverlay": {
                backgroundColor: "rgba(255, 255, 255, 0.8)",
              },
            }}
            components={{
              Toolbar: GridToolbarContainer,
              NoRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ color: "#999" }}
                >
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    No Uploaded Files
                  </Typography>
                </Stack>
              ),
            }}
          />
          <Stack
            sx={{
              position: "absolute",
              bottom: "16px",
              right: "20px",
              zIndex: 10,
              backgroundColor: "#f8f9fa",
              padding: "4px 8px",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <CommonPaginationContainer
              count={paginationCount + 1}
              pageState={pageState}
              setPageState={setPageState}
            />
          </Stack>
        </Box>
      </Box>
    </div>
  );
};

export default TreasuryList;
